// Nutzungsmuster in der Beta (Entscheidung 41): PostHog in der EU, ohne Personenprofile, ohne automatisches Mitschneiden, abschaltbar.
// Eigener kleiner Sender statt posthog-js: lädt keinen fremden Code, setzt keine Cookies und schickt nur, was die Oberfläche ausdrücklich
// meldet (Liste der Ereignisse in docs/NUTZUNGSDATEN.md). Lernschritte selbst stehen schon in learning_events_v2 und kommen hier nicht doppelt.
//  - Kennung (distinct_id): die Nutzer-ID von Supabase, ohne E-Mail und Namen; bis sie da ist, eine zufällige Gerätekennung.
//    So lassen sich die Ereignisse mit den Lerndaten zusammenführen. Die Gerätekennung geht als $device_id immer mit.
//  - $process_person_profile: false (keine Personenprofile), $geoip_disable (kein Ort aus der IP-Adresse).
//  - Abschalter im Konto (gilt für dieses Gerät); ?intern=1 markiert ein Gerät als intern (Arnes Tests), ?intern=0 nimmt das zurück.
// Ohne Schlüssel bleibt alles still. Fehler beim Senden (kein Netz, Dienst weg) stören das Lernen nie.
import { cryptoId } from '../../platform/sync/sync.mjs';

export const KEYS = { off: 'sentenzo.analytics.off', intern: 'sentenzo.analytics.intern', device: 'sentenzo.analytics.device', user: 'sentenzo.analytics.user' };
const MAX_QUEUE = 200, BATCH = 50, DELAY = 5000, RETRY = 30000, WAIT_FOR_USER = 5000, MAX_ERRORS = 5;

// Grobe Geräteangaben mit den Namen, die PostHog für Aufschlüsselungen kennt (posthog-js liefert sie sonst selbst)
export function deviceInfo(ua = '', touchPoints = 0) {
  const ios = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && touchPoints > 1);
  const $os = ios ? 'iOS' : /Android/.test(ua) ? 'Android' : /Windows/.test(ua) ? 'Windows' : /Mac OS X/.test(ua) ? 'Mac OS X' : /CrOS/.test(ua) ? 'Chrome OS' : /Linux/.test(ua) ? 'Linux' : 'Andere';
  const $browser = /Edg(e|A|iOS)?\//.test(ua) ? 'Microsoft Edge' : /SamsungBrowser/.test(ua) ? 'Samsung Internet' : /Firefox|FxiOS/.test(ua) ? 'Firefox'
    : /CriOS|Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? (ios && !/Version\//.test(ua) ? 'Mobile App' : 'Safari') : 'Andere';
  const $device_type = /iPad|Tablet/.test(ua) || (ios && !/iPhone|iPod/.test(ua)) || (/Android/.test(ua) && !/Mobile/.test(ua)) ? 'Tablet' : /Mobi|iPhone|iPod|Android/.test(ua) ? 'Mobile' : 'Desktop';
  return { $os, $browser, $device_type };
}

// Fehlermeldungen ohne Adressen und Mailadressen; nur der Dateiname der Quelle
export function cleanError(message, source) {
  const msg = String(message || '').replace(/[\w.+-]+@[\w-]+\.[\w.-]+/g, '[mail]').replace(/https?:\/\/\S+/g, '[url]').slice(0, 200);
  return { message: msg, source: String(source || '').split(/[?#]/)[0].split('/').pop().slice(0, 80) };
}

const authMethod = u => { const p = u.providers || []; return p.includes('google') ? 'google' : p.includes('apple') ? 'apple' : 'email'; };   // u aus account.js (pick)

// transport: { post(url, body) → Promise<boolean>, beacon(url, body) → boolean }; base: feste Eigenschaften (Kurs, Version, Gerät)
export function createAnalytics({ key = '', host = 'https://eu.i.posthog.com', store, transport, base = {}, page = () => ({}), now = () => Date.now(), timers = { set: (fn, ms) => setTimeout(fn, ms), clear: h => clearTimeout(h) }, dev = false }) {   // Pfeile: setTimeout verlangt window als this
  const url = host.replace(/\/+$/, '') + '/batch/';
  let queue = [], user = null, ready = false, timer = null, sending = false, errors = 0;
  const get = k => { try { return store.get(k); } catch { return null; } };
  const set = (k, v) => { try { if (v === null) store.remove(k); else store.set(k, v); } catch { /* Speicher voll: Statistik ist es nicht wert */ } };
  const available = !!key;
  const device = !available ? null : get(KEYS.device) || (() => { const id = cryptoId(); set(KEYS.device, id); return id; })();
  const enabled = () => available && get(KEYS.off) !== '1';

  function schedule(ms = DELAY) {
    if (timer || !queue.length || !ready) return;
    timer = timers.set(() => { timer = null; flush(); }, ms);
  }
  function stamp(e) {
    return { event: e.event, uuid: e.uuid, timestamp: e.timestamp, properties: { ...base, ...e.properties,
      distinct_id: user ? user.id : device, $device_id: device, account: !user ? 'ohne' : user.anon ? 'anonym' : 'konto',
      internal_tester: dev || get(KEYS.intern) === '1', $process_person_profile: false, $geoip_disable: true } };
  }
  // viaBeacon: beim Verlassen der Seite (pagehide, Tab im Hintergrund) ohne auf Antwort zu warten
  function flush(viaBeacon = false) {
    if (!ready || !queue.length || !enabled() || (sending && !viaBeacon)) return;
    if (timer) { timers.clear(timer); timer = null; }
    const raw = queue.splice(0, BATCH), body = JSON.stringify({ api_key: key, batch: raw.map(stamp) });
    if (viaBeacon) { try { if (transport.beacon(url, body)) return; } catch { /* unten erneut */ } }
    sending = true;
    Promise.resolve().then(() => transport.post(url, body)).catch(() => false).then(ok => {
      sending = false;
      if (!ok) { queue = raw.concat(queue).slice(-MAX_QUEUE); timers.set(() => flush(), RETRY); }
      else if (queue.length) flush();
    });
  }
  function capture(event, props = {}) {
    if (!enabled()) return;
    try {
      queue.push({ event, uuid: cryptoId(), timestamp: new Date(now()).toISOString(), properties: { ...page(), ...props } });
      if (queue.length > MAX_QUEUE) queue.splice(0, queue.length - MAX_QUEUE);
      if (queue.length >= BATCH) flush(); else schedule();
    } catch { /* Statistik darf den Lernablauf nie stören */ }
  }
  // Konto: gleiche ID, vorher anonym, jetzt mit E-Mail oder Google = Konto gesichert; andere ID mit Konto = angemeldet.
  // Der letzte Stand liegt im Speicher, weil Google und der Link aus der Mail die Seite neu laden.
  function setUser(u) {
    if (!available) return;
    const prev = (() => { try { return JSON.parse(get(KEYS.user) || 'null'); } catch { return null; } })();
    user = u ? { id: u.id, anon: !!u.is_anonymous } : null;
    if (user) set(KEYS.user, JSON.stringify(user));
    if (user && !user.anon && prev && prev.anon) capture(prev.id === user.id ? 'account_saved' : 'account_signed_in', { method: authMethod(u) });
    ready = true; schedule();
  }
  // Ohne Server oder ohne Antwort: nach kurzer Wartezeit mit der Gerätekennung senden
  timers.set(() => { if (!ready) { ready = true; schedule(0); } }, WAIT_FOR_USER);

  function error(message, source, line) {
    if (errors >= MAX_ERRORS) return;
    errors++; capture('client_error', { ...cleanError(message, source), line: Number(line) || null });
  }
  function setEnabled(on) {
    set(KEYS.off, on ? null : '1');
    if (!on) { queue = []; if (timer) { timers.clear(timer); timer = null; } }
  }
  function setIntern(on) { set(KEYS.intern, on ? '1' : null); }
  return { available, enabled, capture, flush, setUser, error, setEnabled, setIntern, get queued() { return queue.length; }, get device() { return device; } };
}

// Eine Instanz für die App (main.js legt sie an); vorher und ohne Schlüssel sind alle Aufrufe wirkungslos
let A = null;
export function initAnalytics(opts) { A = createAnalytics(opts); return A; }
export const analytics = {
  get available() { return !!(A && A.available); },
  get enabled() { return !!(A && A.enabled()); },
  setEnabled: on => A && A.setEnabled(on),
  flush: viaBeacon => A && A.flush(viaBeacon),
};
export const track = (event, props) => { if (A) A.capture(event, props); };

// Browser-Anbindung: fetch mit text/plain (keine Vorabanfrage nötig; PostHog liest den JSON-Inhalt trotzdem), beim Verlassen sendBeacon
export function browserTransport() {
  return {
    post: (url, body) => fetch(url, { method: 'POST', body, headers: { 'Content-Type': 'text/plain' }, keepalive: body.length < 60000, credentials: 'omit' }).then(r => r.ok),
    beacon: (url, body) => !!(navigator.sendBeacon && navigator.sendBeacon(url, new Blob([body], { type: 'text/plain' }))),
  };
}
